import React, { useRef, useState } from 'react';
import { Settings, Loader2 } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { TokenPicker, TokenDef } from '../ui/TokenPicker';
import { moduleApi } from '../../lib/api';

const REQUEST_TOKENS: TokenDef[] = [
    { token: '{{evenement}}', desc: "Nom de l'événement" },
    { token: '{{date}}', desc: "Date de l'événement" },
    { token: '{{demandeur}}', desc: 'Qui envoie la demande' },
    { token: '{{bouton}}', desc: 'Bouton vers le booking' },
];
const CALENDAR_REQUEST_TOKENS: TokenDef[] = [
    { token: '{{calendrier}}', desc: 'Mois/année du calendrier' },
    { token: '{{demandeur}}', desc: 'Qui envoie la demande' },
    { token: '{{evenements}}', desc: 'Liste des événements concernés' },
    { token: '{{bouton}}', desc: 'Bouton vers le calendrier' },
];
const CALENDAR_DONE_TOKENS: TokenDef[] = [
    { token: '{{calendrier}}', desc: 'Mois/année du calendrier' },
    { token: '{{booker}}', desc: 'Qui a booké les extras' },
];
const DONE_TOKENS: TokenDef[] = [
    { token: '{{evenement}}', desc: "Nom de l'événement" },
    { token: '{{date}}', desc: "Date de l'événement" },
    { token: '{{booker}}', desc: 'Qui a booké les extras' },
    { token: '{{bouton}}', desc: 'Bouton vers la privatisation' },
];

interface EmailSettings {
    request_subject: string; request_body: string;
    done_subject: string; done_body: string;
    calendar_request_subject: string; calendar_request_body: string;
    calendar_done_subject: string; calendar_done_body: string;
}

/** Réglages des emails de délégation de booking des extras (demande d'Emeni, "tout ces email de notif qui
 *  parte doivent etre configurable") : la demande par événement, et — également configurables, demande
 *  explicite d'Emeni — la demande "calendrier entier" et sa confirmation de retour ("Notifier le calendrier
 *  est à jour"). */
export function BookingEmailSettingsModal({ onClose }: { onClose: () => void }) {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [saved, setSaved] = useState(false);
    const [settings, setSettings] = useState<EmailSettings>({
        request_subject: '', request_body: '',
        done_subject: '', done_body: '',
        calendar_request_subject: '', calendar_request_body: '',
        calendar_done_subject: '', calendar_done_body: '',
    });
    const requestBodyRef = useRef<HTMLTextAreaElement>(null);
    const doneBodyRef = useRef<HTMLTextAreaElement>(null);
    const calendarRequestBodyRef = useRef<HTMLTextAreaElement>(null);
    const calendarDoneBodyRef = useRef<HTMLTextAreaElement>(null);

    React.useEffect(() => {
        moduleApi.getEvenementielBookingEmailSettings()
            .then((s: any) => setSettings({
                request_subject: s.request_subject || '',
                request_body: s.request_body || '',
                done_subject: s.done_subject || '',
                done_body: s.done_body || '',
                calendar_request_subject: s.calendar_request_subject || '',
                calendar_request_body: s.calendar_request_body || '',
                calendar_done_subject: s.calendar_done_subject || '',
                calendar_done_body: s.calendar_done_body || '',
            }))
            .catch((e: any) => setError(e?.message || 'Erreur lors du chargement.'))
            .finally(() => setLoading(false));
    }, []);

    const handleSave = async () => {
        setSaving(true);
        setError('');
        setSaved(false);
        try {
            await moduleApi.updateEvenementielBookingEmailSettings(settings);
            setSaved(true);
        } catch (e: any) {
            setError(e?.message || "Erreur lors de l'enregistrement.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal open onClose={onClose} title="Emails booking des extras" icon={<Settings size={16} />} maxWidth="max-w-lg"
            footer={
                <>
                    <Button variant="secondary" onClick={onClose}>Fermer</Button>
                    <Button variant="primary" onClick={handleSave} disabled={saving || loading}>{saving ? 'Enregistrement...' : 'Enregistrer'}</Button>
                </>
            }>
            {loading ? (
                <div className="flex justify-center py-8"><Loader2 className="animate-spin text-content-muted" size={22} /></div>
            ) : (
                <div className="space-y-5">
                    <div className="space-y-2.5">
                        <h3 className="text-xs font-black text-content uppercase tracking-widest">Demande — un événement</h3>
                        <div>
                            <label className="text-xs font-bold text-content-muted uppercase tracking-widest mb-1.5 block">Sujet</label>
                            <input type="text" value={settings.request_subject} onChange={(e) => setSettings((s) => ({ ...s, request_subject: e.target.value }))}
                                className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500 transition-colors" />
                        </div>
                        <div>
                            <label className="text-xs font-bold text-content-muted uppercase tracking-widest mb-1.5 block">Message</label>
                            <textarea ref={requestBodyRef} value={settings.request_body} onChange={(e) => setSettings((s) => ({ ...s, request_body: e.target.value }))} rows={5}
                                className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500 transition-colors resize-none" />
                        </div>
                        <TokenPicker tokens={REQUEST_TOKENS} textareaRef={requestBodyRef} value={settings.request_body} onChange={(v) => setSettings((s) => ({ ...s, request_body: v }))} />
                    </div>

                    <div className="space-y-2.5 pt-3 border-t border-border">
                        <h3 className="text-xs font-black text-content uppercase tracking-widest">Confirmation — extras internes modifiés</h3>
                        <p className="text-[10px] text-content-muted">Envoyé automatiquement au demandeur à chaque fois que "Booker les extras" change réellement les extras internes bookés — jamais si rien n'a changé.</p>
                        <div>
                            <label className="text-xs font-bold text-content-muted uppercase tracking-widest mb-1.5 block">Sujet</label>
                            <input type="text" value={settings.done_subject} onChange={(e) => setSettings((s) => ({ ...s, done_subject: e.target.value }))}
                                className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500 transition-colors" />
                        </div>
                        <div>
                            <label className="text-xs font-bold text-content-muted uppercase tracking-widest mb-1.5 block">Message</label>
                            <textarea ref={doneBodyRef} value={settings.done_body} onChange={(e) => setSettings((s) => ({ ...s, done_body: e.target.value }))} rows={4}
                                className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500 transition-colors resize-none" />
                        </div>
                        <TokenPicker tokens={DONE_TOKENS} textareaRef={doneBodyRef} value={settings.done_body} onChange={(v) => setSettings((s) => ({ ...s, done_body: v }))} />
                    </div>

                    <div className="space-y-2.5 pt-3 border-t border-border">
                        <h3 className="text-xs font-black text-content uppercase tracking-widest">Demande — calendrier entier</h3>
                        <div>
                            <label className="text-xs font-bold text-content-muted uppercase tracking-widest mb-1.5 block">Sujet</label>
                            <input type="text" value={settings.calendar_request_subject} onChange={(e) => setSettings((s) => ({ ...s, calendar_request_subject: e.target.value }))}
                                className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500 transition-colors" />
                        </div>
                        <div>
                            <label className="text-xs font-bold text-content-muted uppercase tracking-widest mb-1.5 block">Message</label>
                            <textarea ref={calendarRequestBodyRef} value={settings.calendar_request_body} onChange={(e) => setSettings((s) => ({ ...s, calendar_request_body: e.target.value }))} rows={5}
                                className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500 transition-colors resize-none" />
                        </div>
                        <TokenPicker tokens={CALENDAR_REQUEST_TOKENS} textareaRef={calendarRequestBodyRef} value={settings.calendar_request_body} onChange={(v) => setSettings((s) => ({ ...s, calendar_request_body: v }))} />
                    </div>

                    <div className="space-y-2.5 pt-3 border-t border-border">
                        <h3 className="text-xs font-black text-content uppercase tracking-widest">Confirmation — calendrier à jour</h3>
                        <div>
                            <label className="text-xs font-bold text-content-muted uppercase tracking-widest mb-1.5 block">Sujet</label>
                            <input type="text" value={settings.calendar_done_subject} onChange={(e) => setSettings((s) => ({ ...s, calendar_done_subject: e.target.value }))}
                                className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500 transition-colors" />
                        </div>
                        <div>
                            <label className="text-xs font-bold text-content-muted uppercase tracking-widest mb-1.5 block">Message</label>
                            <textarea ref={calendarDoneBodyRef} value={settings.calendar_done_body} onChange={(e) => setSettings((s) => ({ ...s, calendar_done_body: e.target.value }))} rows={4}
                                className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500 transition-colors resize-none" />
                        </div>
                        <TokenPicker tokens={CALENDAR_DONE_TOKENS} textareaRef={calendarDoneBodyRef} value={settings.calendar_done_body} onChange={(v) => setSettings((s) => ({ ...s, calendar_done_body: v }))} />
                    </div>

                    {error && <p className="text-xs text-red-500 font-semibold">{error}</p>}
                    {saved && !error && <p className="text-xs text-brand-600 dark:text-brand-400 font-semibold">Réglages enregistrés.</p>}
                </div>
            )}
        </Modal>
    );
}
